import { Alert, Button, Form, Input } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useChangePasswordMutation, type ChangePasswordRequest } from '@/entities/user';
import { translateApiError } from '@/shared/api/errorToMessage';
import { ROUTES } from '@/shared/config/routes';

export function ChangePasswordForm() {
  const { t } = useTranslation(['auth', 'errors', 'validation']);
  const navigate = useNavigate();
  const [changePassword, { isLoading, error }] = useChangePasswordMutation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordRequest>({ defaultValues: { currentPassword: '', newPassword: '' } });

  const onSubmit = handleSubmit((values) => {
    changePassword(values)
      .unwrap()
      .then(() => void navigate(ROUTES.login, { replace: true }))
      .catch(() => undefined);
  });

  return (
    <Form layout="vertical" onFinish={() => void onSubmit()}>
      <Form.Item
        label={t('auth:changePassword.currentPassword')}
        validateStatus={errors.currentPassword ? 'error' : ''}
        help={errors.currentPassword ? t('validation:required') : undefined}
      >
        <Controller
          name="currentPassword"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input.Password {...field} autoComplete="current-password" autoFocus />
          )}
        />
      </Form.Item>
      <Form.Item
        label={t('auth:changePassword.newPassword')}
        validateStatus={errors.newPassword ? 'error' : ''}
        help={errors.newPassword ? t('validation:passwordMinLength') : undefined}
      >
        <Controller
          name="newPassword"
          control={control}
          rules={{ required: true, minLength: 8 }}
          render={({ field }) => <Input.Password {...field} autoComplete="new-password" />}
        />
      </Form.Item>
      {error ? (
        <Alert
          type="error"
          showIcon
          message={translateApiError(error)}
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <Button type="primary" htmlType="submit" block loading={isLoading}>
        {t('auth:changePassword.submit')}
      </Button>
    </Form>
  );
}
